import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import ThemeToggle from './ThemeToggle';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 36) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/90 dark:bg-slate-900/90 backdrop-blur-md shadow-lg border-b border-gray-200 dark:border-gray-800' 
        : 'bg-white/90 dark:bg-slate-900/90 backdrop-blur-md shadow-lg border-b border-gray-200 dark:border-gray-800'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <a href="/dashboard" className="flex items-center space-x-2 group">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">P</span>
            </div>
            <span className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-purple-600 transition-colors">
              propertyads<span className="text-purple-600">.ai</span>
            </span>
          </a>
          
          {/* Desktop menu */}
          <div className="hidden md:flex items-center space-x-8">
            <a href="/dashboard" className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors text-sm font-medium">
              Home
            </a>
            <a href="#features" className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors text-sm font-medium">
              Features
            </a>
            <a href="#solution" className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors text-sm font-medium">
              How It Works
            </a>
            <a href="#pricing" className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors text-sm font-medium">
              Pricing
            </a>
            <a href="#blog" className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors text-sm font-medium">
              Blog
            </a>
            <a href="#footer" className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors text-sm font-medium">
              Contact
            </a>
            
            {/* Action buttons */}
            <div className="flex items-center space-x-4">
              <a 
                href="/login" 
                className="px-5 py-2 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all text-sm font-medium"
              >
                Log in
              </a>
              <ThemeToggle />
              <a 
                href="/login" 
                className="px-6 py-2.5 text-white font-semibold text-sm rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Start Free
              </a>
            </div>
          </div>
          
          {/* Mobile menu button */}
          <button 
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? 
              <X size={24} className="text-gray-900 dark:text-white" /> : 
              <Menu size={24} className="text-gray-900 dark:text-white" />
            }
          </button>
        </div>
        
        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden pb-6 border-t border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm rounded-b-lg shadow-lg">
            <div className="flex flex-col space-y-4 pt-6">
              <a 
                href="/dashboard" 
                className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors font-medium px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800" 
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </a>
              <a 
                href="#features" 
                className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors font-medium px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800" 
                onClick={() => setIsMenuOpen(false)}
              >
                Features
              </a>
              <a 
                href="#solution" 
                className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors font-medium px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800" 
                onClick={() => setIsMenuOpen(false)}
              >
                How It Works
              </a>
              <a 
                href="#pricing" 
                className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors font-medium px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800" 
                onClick={() => setIsMenuOpen(false)}
              >
                Pricing
              </a>
              <a 
                href="#blog" 
                className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors font-medium px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800" 
                onClick={() => setIsMenuOpen(false)}
              >
                Blog
              </a>
              <a 
                href="#footer" 
                className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors font-medium px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800" 
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </a>
              
              {/* Mobile action buttons */}
              <div className="flex flex-col space-y-3 pt-4 border-t border-gray-200 dark:border-gray-800 mx-4">
                <div className="flex justify-center">
                  <ThemeToggle />
                </div>
                <a 
                  href="/login" 
                  className="text-center px-6 py-3 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Log in
                </a>
                <a 
                  href="/login" 
                  className="text-center px-6 py-3 text-white font-semibold rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg transition-all"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Start Free
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;